// src/views/ShippingFunding.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaFileCsv, FaFilePdf } from 'react-icons/fa';
import './ShippingFunding.css';

/*
SQL DDL (MySQL) suggestion for shipping funding

CREATE TABLE IF NOT EXISTS shipping_funding_config (
  id                INT PRIMARY KEY DEFAULT 1,
  balance_cents     BIGINT NOT NULL DEFAULT 0,
  min_threshold_cents BIGINT NOT NULL DEFAULT 0,
  auto_fund_cents   BIGINT NOT NULL DEFAULT 0,
  last_fund_at      DATETIME NULL,
  last_fund_cents   BIGINT NOT NULL DEFAULT 0,
  provider          VARCHAR(100) NULL,
  account_label     VARCHAR(120) NULL,
  alert_emails      VARCHAR(500) NULL,
  webhook_url       VARCHAR(500) NULL,
  auto_fund_enabled TINYINT(1) NOT NULL DEFAULT 0,
  notes             TEXT NULL,
  updated_at        DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
);

-- Track individual fund/charge events for audit
CREATE TABLE IF NOT EXISTS shipping_funding_txn (
  id           BIGINT AUTO_INCREMENT PRIMARY KEY,
  occurred_at  DATETIME NOT NULL,
  kind         ENUM('FUND','CHARGE','ADJUST') NOT NULL,
  amount_cents BIGINT NOT NULL,
  balance_after_cents BIGINT NOT NULL,
  ref          VARCHAR(120) NULL,
  memo         VARCHAR(500) NULL,
  created_at   DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP
);
*/

const API = import.meta.env.VITE_API_URL;

const toCents = (v) => Math.round(Number(v || 0) * 100);
const fromCents = (c) => (Number(c || 0) / 100).toFixed(2);

function useUSDFormatter() {
  return useMemo(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }), []);
}

function parseCurrencyToCents(input) {
  if (input == null) return 0;
  let s = String(input);
  // Strip currency symbols and spaces; disallow negatives
  s = s.replace(/-/g, '');
  s = s.replace(/[$\s]/g, '');
  // Remove thousand separators
  s = s.replace(/,/g, '');
  // Keep only digits and optional single dot
  const m = s.match(/^(\d+)(?:\.(\d{0,2}))?$/);
  if (!m) {
    const n = Number(s);
    if (Number.isFinite(n)) return Math.max(0, Math.round(n * 100));
    return 0;
  }
  const dollars = m[1] ? Number(m[1]) : 0;
  const cents = m[2] ? Number((m[2] + '00').slice(0, 2)) : 0;
  return Math.max(0, dollars * 100 + cents);
}

function formatCentsUSD(cents, fmt) {
  const n = Number(cents || 0) / 100;
  return fmt.format(n);
}

function MoneyInput({ valueCents, onChangeCents, disabled, placeholder, id, name }) {
  const fmt = useUSDFormatter();
  const [text, setText] = useState(() => formatCentsUSD(valueCents || 0, fmt));
  const isFocusedRef = React.useRef(false);

  useEffect(() => {
    // When parent value changes externally, reflect it if not focused
    if (!isFocusedRef.current) {
      setText(formatCentsUSD(valueCents || 0, fmt));
    }
  }, [valueCents, fmt]);

  const onChange = (e) => {
    let v = e.target.value || '';
    // Remove currency symbol and spaces immediately; block negatives
    v = v.replace(/[$\s-]/g, '');
    setText(v);
    const cents = parseCurrencyToCents(v);
    onChangeCents?.(cents);
  };

  const onBlur = () => {
    isFocusedRef.current = false;
    setText(formatCentsUSD(valueCents || 0, fmt));
  };

  const onFocus = (e) => {
    isFocusedRef.current = true;
    // Show plain number without currency symbol (keep grouping commas)
    const cents = Number(valueCents || 0);
    const n = (cents / 100).toLocaleString('en-US', { useGrouping: true, maximumFractionDigits: 2, minimumFractionDigits: 0 });
    setText(n === '0' ? '' : n);
    // Move caret to end
    requestAnimationFrame(() => {
      try { const el = e.target; el.selectionStart = el.selectionEnd = el.value.length; } catch {}
    });
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      id={id}
      name={name}
      value={text}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

export default function ShippingFunding() {
  const fmtUSD = useUSDFormatter();
  const [cfg, setCfg] = useState({
    balance_cents: 0,
    min_threshold_cents: 0,
    auto_fund_cents: 0,
    last_fund_at: '',
    last_fund_cents: 0,
    provider: '',
    account_label: '',
    alert_emails: '',
    webhook_url: '',
    auto_fund_enabled: false,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addAmountCents, setAddAmountCents] = useState(0);
  const [addRef, setAddRef] = useState('');
  const [addMemo, setAddMemo] = useState('');
  // Build a local time string suitable for <input type="datetime-local">
  function toLocalDateTimeInputValue(date = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const hh = pad(date.getHours());
    const mm = pad(date.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
  }

  const [addWhen, setAddWhen] = useState(() => toLocalDateTimeInputValue());
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Shipping Funding For Juicy WMS Accounts', subtitle: '' }
    }));
    return () => window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const r1 = await fetch(`${API}/api/appconfig/shipping-funding/config`);
      if (r1.ok) {
        const j = await r1.json();
        const c = j || {};
        setCfg({
          balance_cents: Number(c.balance_cents || 0),
          min_threshold_cents: Number(c.min_threshold_cents || 0),
          auto_fund_cents: Number(c.auto_fund_cents || 0),
          last_fund_at: c.last_fund_at || '',
          last_fund_cents: Number(c.last_fund_cents || 0),
          provider: c.provider || '',
          account_label: c.account_label || '',
          alert_emails: c.alert_emails || '',
          webhook_url: c.webhook_url || '',
          auto_fund_enabled: Boolean(c.auto_fund_enabled),
          notes: c.notes || '',
        });
      }
    } catch {}
    try {
      const r2 = await fetch(`${API}/api/appconfig/shipping-funding/transactions?limit=1000`);
      const j2 = await r2.json();
      setTxns(Array.isArray(j2) ? j2 : (Array.isArray(j2?.rows) ? j2.rows : []));
    } catch { setTxns([]); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...cfg,
        // ensure numeric cents
        balance_cents: Number(cfg.balance_cents || 0),
        min_threshold_cents: Number(cfg.min_threshold_cents || 0),
        auto_fund_cents: Number(cfg.auto_fund_cents || 0),
        last_fund_cents: Number(cfg.last_fund_cents || 0),
      };
      const res = await fetch(`${API}/api/appconfig/shipping-funding/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Failed to save');
      }
      alert('Saved');
      await loadAll();
      setEditMode(false);
    } catch (e) {
      alert(e.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const addFunds = async () => {
    const cents = Number(addAmountCents || 0);
    if (!cents || cents <= 0) { alert('Enter a positive amount'); return; }
    setAddBusy(true);
    try {
      const res = await fetch(`${API}/api/appconfig/shipping-funding/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'FUND', amount_cents: cents, occurred_at: addWhen ? new Date(addWhen).toISOString() : undefined, ref: addRef || undefined, memo: addMemo || undefined })
      });
      if (!res.ok) {
        const e = await res.json().catch(()=>({}));
        throw new Error(e.error || 'Failed to add funds');
      }
      setAddOpen(false);
      setAddAmountCents(0); setAddRef(''); setAddMemo(''); setAddWhen(toLocalDateTimeInputValue());
      await loadAll();
    } catch (e) {
      alert(e.message || 'Failed to add funds');
    } finally { setAddBusy(false); }
  };

  const [reconciling, setReconciling] = useState(false);
  const reconcile = async () => {
    if (editMode) return; // don't reconcile while editing form
    setReconciling(true);
    try {
      const res = await fetch(`${API}/api/appconfig/shipping-funding/reconcile`, { method: 'POST' });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || j?.success === false) throw new Error(j?.error || 'Failed to reconcile');
      await loadAll();
    } catch (e) {
      alert(e.message || 'Failed to reconcile');
    } finally { setReconciling(false); }
  };

  // ====== All Activity: client-side sorting (persisted) ======
  const LS_ALL_SORT_FIELD = 'shipfund.all.sortField';
  const LS_ALL_SORT_ORDER = 'shipfund.all.sortOrder';
  const [allSortField, setAllSortField] = useState(() => localStorage.getItem(LS_ALL_SORT_FIELD) || 'occurred_at');
  const [allSortOrder, setAllSortOrder] = useState(() => localStorage.getItem(LS_ALL_SORT_ORDER) || 'DESC');
  useEffect(() => { try { localStorage.setItem(LS_ALL_SORT_FIELD, allSortField); } catch {} }, [allSortField]);
  useEffect(() => { try { localStorage.setItem(LS_ALL_SORT_ORDER, allSortOrder); } catch {} }, [allSortOrder]);

  const numericFields = new Set(['elabelid','amount_cents','balance_after_cents','eorderid','shipping_integration_id','id']);
  const dateFields    = new Set(['occurred_at','created_at']);
  const getVal = (row, field) => {
    const v = row?.[field];
    if (v == null) return null;
    if (numericFields.has(field)) return Number(v) || 0;
    if (dateFields.has(field)) {
      const t = Date.parse(v);
      return Number.isFinite(t) ? t : 0;
    }
    // Derived field: ship_to
    if (field === 'ship_to') {
      return String(formatShipTo(row)).toLowerCase();
    }
    return String(v).toLowerCase();
  };
  const sortedTxns = React.useMemo(() => {
    const arr = Array.isArray(txns) ? [...txns] : [];
    const field = allSortField || 'occurred_at';
    const order = (String(allSortOrder || 'DESC').toUpperCase() === 'ASC') ? 1 : -1;
    arr.sort((a, b) => {
      const va = getVal(a, field);
      const vb = getVal(b, field);
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls last
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * order;
      // string compare
      return String(va).localeCompare(String(vb)) * order;
    });
    return arr;
  }, [txns, allSortField, allSortOrder]);

  // Build single-line 'Ship To' from eorder fields present on each transaction row
  const formatShipTo = (t) => {
    if (!t || !t.eorderid) return '—';
    const name = (t.ship_company && String(t.ship_company).trim())
      ? String(t.ship_company).trim()
      : `${String(t.ship_first || '').trim()} ${String(t.ship_last || '').trim()}`.trim();
    const cityStatePostal = [
      String(t.ship_city || '').trim() || null,
      String(t.ship_state || '').trim() || null,
      String(t.ship_postal || '').trim() || null,
    ].filter(Boolean).join(', ');
    const parts = [
      name || null,
      String(t.ship_addr1 || '').trim() || null,
      String(t.ship_addr2 || '').trim() || null,
      cityStatePostal || null,
      String(t.ship_country || '').trim() || null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : '—';
  };

  // Shared CSV cell escaper
  const toCsvCell = (v) => {
    const s = (v == null) ? '' : String(v);
    const esc = s.replace(/"/g, '""');
    return /[",\n]/.test(esc) ? `"${esc}"` : esc;
  };

  // ===== Export helpers (Recent Funding Activity) =====
  const exportFundCSV = () => {
    try {
      const headers = ['Date', 'Type', 'Amount (USD)', 'Ref', 'Memo'];
      const funds = (txns || []).filter(
        t => String(t.kind || '').toUpperCase() === 'FUND'
      );
      const rows = funds.map(t => [
        t.occurred_at ? new Date(t.occurred_at).toLocaleString() : '',
        t.kind || '',
        (Number(t.amount_cents || 0) / 100).toFixed(2),
        t.ref || '',
        t.memo || ''
      ]);
      const csv = [
        headers.map(toCsvCell).join(','),
        ...rows.map(r => r.map(toCsvCell).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipping_funding_recent_funds_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Failed to export CSV');
    }
  };

  const exportFundPDF = () => {
    const funds = Array.isArray(txns)
      ? txns.filter(t => String(t.kind || '').toUpperCase() === 'FUND')
      : [];
    const lim = funds.length || 100;
    const params = new URLSearchParams({
      limit: String(lim),
      sort_field: 'occurred_at',
      sort_order: 'DESC',
      kind: 'FUND',
    });
    const url = `${API}/api/appconfig/shipping-funding/transactions/pdf?${params.toString()}`;
    try { window.open(url, '_blank', 'noopener,noreferrer'); }
    catch { window.location.href = url; }
  };

  // ===== Export helpers (All Activity) =====
  const exportAllCSV = () => {
    try {
      const headers = [
        'Date', 'Type', 'Amount (USD)', 'Balance After (USD)',
        'E-Order ID', 'Label ID', 'Shipping Profile', 'Client', 'Ship To', 'Ref', 'Memo'
      ];
      const rows = (sortedTxns || []).map(t => [
        t.occurred_at ? new Date(t.occurred_at).toLocaleString() : '',
        t.kind || '',
        (Number(t.amount_cents || 0) / 100).toFixed(2),
        (Number(t.balance_after_cents || 0) / 100).toFixed(2),
        t.eorderid ?? '',
        t.elabelid ?? '',
        t.integration_displayname || '',
        t.client_name || '',
        formatShipTo(t),
        t.ref || '',
        t.memo || ''
      ]);
      const csv = [headers.map(toCsvCell).join(','), ...rows.map(r => r.map(toCsvCell).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipping_funding_all_activity_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || 'Failed to export CSV');
    }
  };
  const exportAllPDF = () => {
    const lim = Array.isArray(sortedTxns) ? sortedTxns.length : 100;
    const params = new URLSearchParams({
      limit: String(lim || 100),
      sort_field: allSortField || 'occurred_at',
      sort_order: allSortOrder || 'DESC',
    });
    const url = `${API}/api/appconfig/shipping-funding/transactions/pdf?${params.toString()}`;
    try { window.open(url, '_blank', 'noopener,noreferrer'); }
    catch { window.location.href = url; }
  };

  const handleAllSort = (field, e) => {
    e?.preventDefault?.();
    if (allSortField === field) {
      setAllSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setAllSortField(field);
      // Default: for date fields and numeric amounts, sort DESC; otherwise ASC
      if (dateFields.has(field) || numericFields.has(field)) setAllSortOrder('DESC'); else setAllSortOrder('ASC');
    }
  };

  return (
    <>
    <div className="shipfund-wrap">
      <div className="shipfund-card shipfund-config">
        <h2>Funding Configuration</h2>
        {loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <div className="form-grid">
            <label>
              <span>Current Balance</span>
              <input type="text" value={fmtUSD.format(Number(cfg.balance_cents || 0) / 100)} disabled />
            </label>
            <label>
              <span>Minimum Threshold</span>
              <MoneyInput
                valueCents={cfg.min_threshold_cents}
                onChangeCents={(c)=>setCfg({...cfg, min_threshold_cents: c})}
                disabled={!editMode}
              />
            </label>
            <label>
              <span>Auto Fund Amount</span>
              <MoneyInput
                valueCents={cfg.auto_fund_cents}
                onChangeCents={(c)=>setCfg({...cfg, auto_fund_cents: c})}
                disabled={!editMode}
              />
            </label>
            <label className="checkbox-inline">
              <input type="checkbox" checked={!!cfg.auto_fund_enabled} disabled={!editMode} onChange={(e)=>setCfg({...cfg, auto_fund_enabled: e.target.checked})} />
              <span>Auto-Fund Enabled</span>
            </label>
            {/* Removed Last Fund Date and Last Fund Amount from UI per request */}
            <label>
              <span>Funding Provider</span>
              <input type="text" value={cfg.provider} disabled={!editMode} onChange={(e)=>setCfg({...cfg, provider: e.target.value})} />
            </label>
            <label>
              <span>Account Label</span>
              <input type="text" value={cfg.account_label} disabled={!editMode} onChange={(e)=>setCfg({...cfg, account_label: e.target.value})} />
            </label>
            <label className="full">
              <span>Alert Emails (comma separated)</span>
              <input type="text" value={cfg.alert_emails} disabled={!editMode} onChange={(e)=>setCfg({...cfg, alert_emails: e.target.value})} />
            </label>
            <label className="full">
              <span>Low-Balance Webhook URL</span>
              <input type="text" value={cfg.webhook_url} disabled={!editMode} onChange={(e)=>setCfg({...cfg, webhook_url: e.target.value})} />
            </label>
            {/* Removed Notes (use Add Funds memo instead) */}
          </div>
        )}
        <div className="actions">
          <button className="btn" onClick={() => setAddOpen(true)} disabled={loading || editMode}>Add Funds</button>
          {!editMode ? (
            <>
              <button className="btn" onClick={() => setEditMode(true)} disabled={loading}>Edit</button>
              <button className="btn" onClick={reconcile} disabled={loading || reconciling}>{reconciling ? 'Reconciling…' : 'Reconcile'}</button>
            </>
          ) : (
            <>
              <button className="btn" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-secondary" onClick={async ()=>{ setEditMode(false); await loadAll(); }} disabled={saving}>Cancel</button>
            </>
          )}
        </div>
      </div>

      <div className="shipfund-row">
        <div className="shipfund-card">
          <div className="sf-card-head">
            <h2>Recent Funding Activity</h2>
            <div className="sf-actions">
              <button className="mini-btn" title="Export CSV" onClick={exportFundCSV}>
                <FaFileCsv />
              </button>
              <button className="mini-btn" title="Export PDF" onClick={exportFundPDF}>
                <FaFilePdf />
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table className="shipfund-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th className="td-right">Amount</th>
                  <th>Ref</th>
                  <th>Memo</th>
                </tr>
              </thead>
              <tbody>
                {txns?.length ? txns
                  .filter(t => String(t.kind || '').toUpperCase() === 'FUND')
                  .map((t, i) => (
                    <tr key={t.id || i}>
                      <td>{t.occurred_at ? new Date(t.occurred_at).toLocaleString() : '—'}</td>
                      <td>{t.kind || '—'}</td>
                      <td className="td-right">{`$${fromCents(t.amount_cents || 0)}`}</td>
                      <td>{t.ref || '—'}</td>
                      <td>{t.memo || '—'}</td>
                    </tr>
                  )) : (
                  <tr><td colSpan={5} style={{ opacity: .7 }}>
                    {loading ? 'Loading…' : 'No activity yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="shipfund-card">
          <div className="sf-card-head">
            <h2>All Activity</h2>
            <div className="sf-actions">
              <button className="mini-btn" title="Export CSV" onClick={exportAllCSV}>
                <FaFileCsv />
              </button>
              <button className="mini-btn" title="Export PDF" onClick={exportAllPDF}>
                <FaFilePdf />
              </button>
            </div>
          </div>
          <div className="subtle-note">E-Order ID and Label ID are only recorded for label charge/adjust transactions; funding entries will show “—”.</div>
          <div className="table-wrap">
            <table className="shipfund-table">
              <thead>
                <tr>
                  <th
                    onClick={(e)=>handleAllSort('eorderid', e)}
                    className={allSortField==='eorderid' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    E-Order ID {allSortField==='eorderid' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('elabelid', e)}
                    className={allSortField==='elabelid' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Label ID {allSortField==='elabelid' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('occurred_at', e)}
                    className={allSortField==='occurred_at' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Date {allSortField==='occurred_at' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('kind', e)}
                    className={allSortField==='kind' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Type {allSortField==='kind' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('amount_cents', e)}
                    className={allSortField==='amount_cents' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Amount {allSortField==='amount_cents' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('balance_after_cents', e)}
                    className={allSortField==='balance_after_cents' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Balance After {allSortField==='balance_after_cents' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('integration_displayname', e)}
                    className={allSortField==='integration_displayname' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Shipping Profile {allSortField==='integration_displayname' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('client_name', e)}
                    className={allSortField==='client_name' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Client {allSortField==='client_name' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('ship_to', e)}
                    className={allSortField==='ship_to' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Ship To {allSortField==='ship_to' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('ref', e)}
                    className={allSortField==='ref' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Ref {allSortField==='ref' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                  <th
                    onClick={(e)=>handleAllSort('memo', e)}
                    className={allSortField==='memo' ? 'active-sort' : ''}
                    style={{ cursor:'pointer', userSelect:'none' }}
                  >
                    Memo {allSortField==='memo' ? (allSortOrder==='ASC' ? '▲':'▼') : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTxns?.length ? sortedTxns.map((t, i) => (
                  <tr key={t.id || i} className={t.deleted ? 'deleted-txn' : undefined}>
                    <td title={String(t.kind).toUpperCase() === 'FUND' ? 'Not applicable for FUND' : (!t.eorderid ? 'Not recorded on this transaction' : undefined)}>
                      {t.eorderid ?? '—'}
                    </td>
                    <td title={String(t.kind).toUpperCase() === 'FUND' ? 'Not applicable for FUND' : (!t.elabelid ? 'Not recorded on this transaction' : undefined)}>
                      {t.elabelid ?? '—'}
                    </td>
                    <td>{t.occurred_at ? new Date(t.occurred_at).toLocaleString() : '—'}</td>
                    <td>{t.deleted ? 'VOIDED' : (t.kind || '—')}</td>
                      <td className="td-right">{`$${fromCents(t.amount_cents || 0)}`}</td>
                    <td className="td-right">{`$${fromCents(t.balance_after_cents || 0)}`}</td>
                    <td>{t.integration_displayname || '—'}</td>
                    <td>{t.client_name || '—'}</td>
                    <td>{formatShipTo(t)}</td>
                    <td>{t.ref || '—'}</td>
                    <td>{t.memo || '—'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={11} style={{ opacity: .7 }}>
                    {loading ? 'Loading…' : 'No activity yet.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    {addOpen && (
      <div className="eml-modal__overlay" role="dialog" aria-modal="true" aria-labelledby="addfund-title" onClick={(e)=>{ if (e.target.classList.contains('eml-modal__overlay')) setAddOpen(false); }}>
        <div className="eml-modal" style={{ maxWidth: 420 }}>
          <div className="eml-modal__header">
            <h2 id="addfund-title">Add Funds</h2>
            <button className="eml-modal__close" onClick={()=>setAddOpen(false)} aria-label="Close">✕</button>
          </div>
          <div className="eml-modal__body">
            <div className="eml-form">
              <label>
                <span>Amount (USD)</span>
                <MoneyInput
                  valueCents={addAmountCents}
                  onChangeCents={setAddAmountCents}
                />
              </label>
              <label>
                <span>When</span>
                <input type="datetime-local" value={addWhen} onChange={(e)=>setAddWhen(e.target.value)} />
              </label>
              <label>
                <span>Reference #</span>
                <input type="text" value={addRef} onChange={(e)=>setAddRef(e.target.value)} />
              </label>
              <label>
                <span>Memo</span>
                <input type="text" value={addMemo} onChange={(e)=>setAddMemo(e.target.value)} />
              </label>
            </div>
          </div>
          <div className="eml-modal__footer">
            <button className="btn" onClick={addFunds} disabled={addBusy}>{addBusy ? 'Adding…' : 'Add Funds'}</button>
            <button className="btn btn-secondary" onClick={()=>setAddOpen(false)} disabled={addBusy}>Cancel</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
